import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, productImage, STATIC_BASE } from "@/lib/api";
import { formatPrice } from "@/lib/currency";
import DualPrice from "@/components/DualPrice";
import { useI18n } from "@/contexts/I18nContext";
import { useCart } from "@/contexts/CartContext";
import { useWishlist } from "@/contexts/WishlistContext";
import { TID } from "@/constants/testIds";
import ProductCard from "@/components/ProductCard";
import { Minus, Plus, ChevronRight, Heart, Truck, Shield, Package, Feather } from "lucide-react";

export default function ProductDetail() {
    const { slug } = useParams();
    const nav = useNavigate();
    const { t, tr, lang } = useI18n();
    const { add } = useCart();
    const { has, toggle } = useWishlist();
    const [product, setProduct] = useState(null);
    const [pair, setPair] = useState(null);
    const [qty, setQty] = useState(1);
    const [related, setRelated] = useState([]);
    const [activeIdx, setActiveIdx] = useState(0);

    useEffect(() => {
        window.scrollTo(0, 0);
        setActiveIdx(0);
        setPair(null);
        api.get(`/products/${slug}`).then(({ data }) => {
            setProduct(data);
            api.get(`/products?vertical=${data.vertical}`).then(({ data: rel }) => {
                setRelated(rel.filter((r) => r.slug !== data.slug).slice(0, 4));
            });
            // Fetch companion piece if configured
            if (data.pair_slug) {
                api.get(`/products/${data.pair_slug}`).then(({ data: p }) => setPair(p)).catch(() => setPair(null));
            }
        }).catch(() => nav("/shop"));
    }, [slug, nav]);

    // Gallery composition: hero first, then gallery_images
    const gallery = useMemo(() => {
        if (!product) return [];
        const primary = productImage(product);
        const extras = (product.gallery_images || []).map((g) => `${STATIC_BASE}/${g}`);
        return [primary, ...extras];
    }, [product]);

    if (!product) return <div className="py-40 text-center text-bone-300 text-sm tracking-widest">{t("common.loading")}</div>;

    const badge = product.badge ? (product.badge[lang] || product.badge.en) : null;
    const saved = has(product.slug);
    const verticalName = product.vertical === "masalas" ? "The Spice House" : product.vertical === "jewelry" ? "The Jewel House" : "The Fabric House";
    const verticalTitle = product.vertical === "masalas" ? t("vert.masalas.title") : product.vertical === "jewelry" ? t("vert.jewelry.title") : t("vert.decor.title");

    return (
        <div className="pt-6 sm:pt-8 pb-16 sm:pb-24">
            <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-10">
                {/* Breadcrumb */}
                <nav className="flex items-center gap-1.5 sm:gap-2 text-[9px] sm:text-[10px] tracking-[0.24em] uppercase text-bone-300/70 mb-6 sm:mb-10 flex-wrap">
                    <Link to="/" className="hover:text-brass-400 transition-colors">Home</Link>
                    <ChevronRight className="w-3 h-3" />
                    <Link to={`/shop/${product.vertical}`} className="hover:text-brass-400 transition-colors">
                        {verticalTitle}
                    </Link>
                    <ChevronRight className="w-3 h-3" />
                    <span className="text-bone-100 truncate max-w-[200px]">{tr(product.name)}</span>
                </nav>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-12 lg:gap-20">
                    {/* GALLERY */}
                    <div className="lg:col-span-7">
                        <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 sm:gap-4">
                            {/* Thumbnails (desktop side, mobile below) */}
                            {gallery.length > 1 && (
                                <div className="hidden sm:flex sm:col-span-2 flex-col gap-3 order-1">
                                    {gallery.map((src, i) => (
                                        <button
                                            key={i}
                                            data-testid={`pd-thumb-${i}`}
                                            onClick={() => setActiveIdx(i)}
                                            className={`aspect-square bg-ink-800 overflow-hidden border-2 transition-colors ${activeIdx === i ? "border-brass-400" : "border-transparent hover:border-white/30"}`}
                                        >
                                            <img src={src} alt="" className="w-full h-full object-cover" />
                                        </button>
                                    ))}
                                </div>
                            )}
                            {/* Main image */}
                            <div className={`relative bg-ink-800 aspect-[4/5] overflow-hidden order-2 ${gallery.length > 1 ? "sm:col-span-10" : "sm:col-span-12"}`}>
                                <img src={gallery[activeIdx]} alt={tr(product.name)} className="w-full h-full object-cover" />
                                {badge && (
                                    <span className="absolute top-4 sm:top-6 left-4 sm:left-6 bg-ink-900/85 backdrop-blur border border-brass-400/40 text-brass-400 text-[10px] tracking-[0.24em] uppercase px-3 py-1.5">
                                        {badge}
                                    </span>
                                )}
                                <button
                                    onClick={() => toggle(product.slug)}
                                    data-testid="pd-wishlist"
                                    className={`absolute top-4 sm:top-6 right-4 sm:right-6 w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center backdrop-blur border transition-colors ${
                                        saved ? "bg-brass-400 text-ink-900 border-brass-400" : "bg-ink-900/70 text-bone-100 border-white/15 hover:border-brass-400 hover:text-brass-400"
                                    }`}
                                    aria-label="Toggle wishlist"
                                >
                                    <Heart className={`w-5 h-5 ${saved ? "fill-current" : ""}`} strokeWidth={1.5} />
                                </button>
                            </div>
                            {/* Mobile thumbnails */}
                            {gallery.length > 1 && (
                                <div className="sm:hidden flex gap-3 overflow-x-auto scrollbar-hide order-3 -mx-5 px-5">
                                    {gallery.map((src, i) => (
                                        <button
                                            key={i}
                                            data-testid={`pd-thumb-m-${i}`}
                                            onClick={() => setActiveIdx(i)}
                                            className={`w-20 h-20 shrink-0 bg-ink-800 overflow-hidden border-2 transition-colors ${activeIdx === i ? "border-brass-400" : "border-transparent"}`}
                                        >
                                            <img src={src} alt="" className="w-full h-full object-cover" />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* DETAILS */}
                    <div className="lg:col-span-5 lg:pt-4">
                        <p className="text-[10px] sm:text-[11px] tracking-[0.32em] uppercase text-brass-400 mb-4 sm:mb-5">
                            {verticalName}
                        </p>
                        <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight text-bone-100 mb-4">
                            {tr(product.name)}
                        </h1>
                        <div className="flex items-baseline gap-3 sm:gap-4 mb-6 sm:mb-8">
                            <DualPrice amount={product.price_eur} size="xl" inline />
                            <span className="text-xs tracking-[0.2em] uppercase text-bone-300/70">/ {product.unit}</span>
                        </div>

                        <p className="text-base text-bone-100/85 leading-relaxed mb-5 sm:mb-6 font-light">
                            {tr(product.short_description)}
                        </p>
                        <p className="text-sm sm:text-base text-bone-300 leading-relaxed mb-8 sm:mb-10 font-light">
                            {tr(product.long_description)}
                        </p>

                        {/* Quantity + Add */}
                        <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
                            <div className="flex items-center border border-white/15">
                                <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-10 sm:w-11 h-12 flex items-center justify-center text-bone-100/70 hover:text-brass-400 transition-colors" aria-label="Decrease">
                                    <Minus className="w-4 h-4" />
                                </button>
                                <span data-testid={TID.pdQty} className="w-10 sm:w-12 text-center text-bone-100 tabular-nums">{qty}</span>
                                <button onClick={() => setQty((q) => Math.min(50, q + 1))} className="w-10 sm:w-11 h-12 flex items-center justify-center text-bone-100/70 hover:text-brass-400 transition-colors" aria-label="Increase">
                                    <Plus className="w-4 h-4" />
                                </button>
                            </div>
                            <button
                                data-testid={TID.pdAddCart}
                                onClick={() => add(product, qty)}
                                className="flex-1 border border-brass-400 text-brass-400 hover:bg-brass-400 hover:text-ink-900 transition-colors duration-500 py-3.5 text-[10px] sm:text-[11px] tracking-[0.24em] sm:tracking-[0.28em] uppercase"
                            >
                                {t("prod.addCart")}
                            </button>
                        </div>
                        <button
                            data-testid={TID.pdBuyNow}
                            onClick={() => { add(product, qty); nav("/cart"); }}
                            className="w-full bg-brass-400 text-ink-900 hover:bg-brass-300 py-3.5 text-[10px] sm:text-[11px] tracking-[0.24em] sm:tracking-[0.28em] uppercase transition-colors mb-8 sm:mb-10"
                        >
                            {t("prod.buyNow")}
                        </button>

                        {/* Trust rail */}
                        <div className="grid grid-cols-3 gap-3 sm:gap-4 pb-8 sm:pb-10 border-b border-white/10">
                            <div className="text-center">
                                <Truck className="w-4 h-4 sm:w-5 sm:h-5 text-brass-400 mx-auto mb-2" strokeWidth={1.5} />
                                <p className="text-[9px] sm:text-[10px] tracking-[0.2em] uppercase text-bone-300">EU 3-5 days</p>
                            </div>
                            <div className="text-center">
                                <Package className="w-4 h-4 sm:w-5 sm:h-5 text-brass-400 mx-auto mb-2" strokeWidth={1.5} />
                                <p className="text-[9px] sm:text-[10px] tracking-[0.2em] uppercase text-bone-300">Gift wrapped</p>
                            </div>
                            <div className="text-center">
                                <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-brass-400 mx-auto mb-2" strokeWidth={1.5} />
                                <p className="text-[9px] sm:text-[10px] tracking-[0.2em] uppercase text-bone-300">30-day returns</p>
                            </div>
                        </div>

                        <div className="pt-6 sm:pt-8 space-y-3 text-sm text-bone-300 font-light">
                            <div className="flex justify-between"><span className="text-bone-100/60 uppercase text-[10px] tracking-[0.24em]">Format</span><span>{product.unit}</span></div>
                            <div className="flex justify-between"><span className="text-bone-100/60 uppercase text-[10px] tracking-[0.24em]">Origin</span><span>India — Handcrafted</span></div>
                            <div className="flex justify-between"><span className="text-bone-100/60 uppercase text-[10px] tracking-[0.24em]">Shipping</span><span>Complimentary above €120</span></div>
                        </div>
                    </div>
                </div>

                {/* Sold-in-Pairs Bundling */}
                {pair && (
                    <div data-testid="pd-pair-bundle" className="mt-20 sm:mt-28 border-y border-brass-400/20 py-12 sm:py-16">
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
                            <div className="lg:col-span-4">
                                <p className="text-[10px] sm:text-[11px] tracking-[0.32em] uppercase text-brass-400 mb-3">— Better together</p>
                                <h3 className="font-serif text-3xl sm:text-4xl text-bone-100 leading-tight mb-4">
                                    Complete the styling.
                                </h3>
                                <p className="text-sm sm:text-base text-bone-300 font-light leading-relaxed italic mb-6">
                                    {tr(product.pair_note || {})}
                                </p>
                                {(() => {
                                    const combined = (product.price_eur || 0) + (pair.price_eur || 0);
                                    const discounted = combined * 0.9;
                                    return (
                                        <div>
                                            <div className="mb-5">
                                                <div className="flex items-baseline gap-3">
                                                    <span className="text-[10px] tracking-[0.24em] uppercase text-bone-300/70">Both pieces</span>
                                                    <DualPrice amount={discounted} size="lg" inline />
                                                    <span className="text-xs text-bone-300/60 line-through">{formatPrice(combined, lang)}</span>
                                                </div>
                                                <p className="mt-2 text-[10px] tracking-[0.28em] uppercase text-brass-400">— Bundle · 10% off applied</p>
                                            </div>
                                            <button
                                                data-testid="pd-add-both"
                                                onClick={() => { add(product, 1); add(pair, 1); }}
                                                className="w-full sm:w-auto bg-brass-400 text-ink-900 hover:bg-brass-300 px-6 py-3.5 text-[10px] sm:text-[11px] tracking-[0.24em] sm:tracking-[0.28em] uppercase transition-colors"
                                            >
                                                Add both to cart
                                            </button>
                                        </div>
                                    );
                                })()}
                            </div>
                            <div className="lg:col-span-8">
                                <div className="grid grid-cols-2 gap-4 sm:gap-6 items-center">
                                    <Link to={`/product/${product.slug}`} className="group">
                                        <div className="aspect-[4/5] card-image-wrap bg-ink-800">
                                            <img src={productImage(product)} alt={tr(product.name)} className="w-full h-full object-cover" />
                                        </div>
                                        <p className="mt-3 text-xs sm:text-sm text-bone-100 font-serif">{tr(product.name)}</p>
                                        <p className="text-[10px] sm:text-xs text-brass-400">{formatPrice(product.price_eur, lang)}</p>
                                    </Link>
                                    <div className="relative">
                                        <Link to={`/product/${pair.slug}`} className="group block">
                                            <div className="aspect-[4/5] card-image-wrap bg-ink-800">
                                                <img src={productImage(pair)} alt={tr(pair.name)} className="w-full h-full object-cover" />
                                            </div>
                                            <p className="mt-3 text-xs sm:text-sm text-bone-100 font-serif">{tr(pair.name)}</p>
                                            <p className="text-[10px] sm:text-xs text-brass-400">{formatPrice(pair.price_eur, lang)}</p>
                                        </Link>
                                        <span className="absolute -left-4 sm:-left-5 top-[40%] -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 bg-ink-900 border border-brass-400 text-brass-400 font-serif italic text-lg sm:text-xl flex items-center justify-center rounded-full">+</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Story Behind the Piece */}
                {product.maker_note && (
                    <div data-testid="pd-story" className="mt-20 sm:mt-28">
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center">
                            <div className="lg:col-span-6">
                                <div className="relative aspect-[4/5] card-image-wrap bg-ink-800 border border-brass-400/15">
                                    <img
                                        src={product.story_image ? `${STATIC_BASE}/${product.story_image}` : productImage(product)}
                                        alt={`${tr(product.name)} — atelier detail`}
                                        className="w-full h-full object-cover"
                                    />
                                    <div className="pointer-events-none absolute inset-0">
                                        <div className="absolute top-0 left-0 w-6 h-6 border-t border-l border-brass-400/70" />
                                        <div className="absolute top-0 right-0 w-6 h-6 border-t border-r border-brass-400/70" />
                                        <div className="absolute bottom-0 left-0 w-6 h-6 border-b border-l border-brass-400/70" />
                                        <div className="absolute bottom-0 right-0 w-6 h-6 border-b border-r border-brass-400/70" />
                                    </div>
                                </div>
                            </div>
                            <div className="lg:col-span-6 lg:pl-8">
                                <div className="flex items-center gap-3 mb-5">
                                    <Feather className="w-4 h-4 text-brass-400" strokeWidth={1.5} />
                                    <p className="text-[10px] sm:text-[11px] tracking-[0.32em] uppercase text-brass-400">— The maker's note</p>
                                </div>
                                <h3 className="font-serif italic text-3xl sm:text-4xl lg:text-5xl text-bone-100 leading-[1.05] mb-6 sm:mb-8">
                                    Six months on the frame.
                                </h3>
                                {String(tr(product.maker_note)).split("\n\n").map((para, i) => (
                                    <p key={i} className="text-base sm:text-lg text-bone-100/85 font-light leading-relaxed mb-4">
                                        {para}
                                    </p>
                                ))}
                                <div className="mt-8 pt-6 border-t border-white/10 flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-full bg-brass-400/20 border border-brass-400/40 flex items-center justify-center text-brass-400 font-serif italic">R</div>
                                    <div>
                                        <p className="text-sm text-bone-100 font-serif">Rehmat Kumar</p>
                                        <p className="text-[10px] tracking-[0.24em] uppercase text-bone-300/70">Master embroiderer · Varanasi</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Related */}
                {related.length > 0 && (
                    <div className="mt-20 sm:mt-32">
                        <h2 className="font-serif text-3xl sm:text-4xl text-bone-100 mb-8 sm:mb-12">{t("prod.related")}</h2>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 sm:gap-x-8 gap-y-10 sm:gap-y-12">
                            {related.map((p) => <ProductCard key={p.slug} product={p} />)}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
